import classNames from "classnames";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useFreeSpaceFinder } from "src/app/methods/useFreeSpaceFinder";
import { DROPDOWN_OPEN_EVENT } from "src/utils/dropdownEvents";
import useOnClickOutside from "../../methods/useOnClickOutside";
import "./Dropdown.scss";

enum DropdownSize {
  Default = "default",
  Big = "big",
}

interface DropdownProps {
  toRight?: boolean;
  alignRight?: boolean;
  alignBottom?: boolean;
  toBottom?: boolean;
  fromRight?: boolean;
  block?: boolean;
  toggleGlobalState?: boolean;
  size?: "default" | "big";
  className?: string;
  children: (
    value: boolean,
    setterFunction: Dispatch<SetStateAction<boolean>>,
  ) => React.ReactNode;
  outsideClickCallback?: () => void;
  disableOutsideClick?: boolean;
  disableGlobalCloseEvent?: boolean; // Don't send/receive DROPDOWN_OPEN_EVENT (for nested dropdowns)
  /** Fired whenever isOpen changes, regardless of how it closed (outside click, menu item
   * click, Escape, etc). Prefer this over outsideClickCallback for state that must always
   * stay in sync with the dropdown's open state - outsideClickCallback only fires for one
   * specific close path and silently goes stale if the dropdown is closed another way. */
  onOpenChange?: (isOpen: boolean) => void;
}

export function Dropdown({
  toRight,
  alignRight,
  alignBottom,
  toBottom,
  fromRight,
  toggleGlobalState,
  block,
  children,
  size,
  className,
  outsideClickCallback,
  disableOutsideClick,
  disableGlobalCloseEvent,
  onOpenChange,
}: DropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownIdRef = useRef(Math.random().toString(36).slice(2));
  const [isOpen, setIsOpen] = useState(false);

  useOnClickOutside(dropdownRef, () => {
    if (!isOpen || disableOutsideClick) return;

    setIsOpen(false);

    if (toggleGlobalState) {
      document.body.classList.remove("dd-active");
    }

    if (outsideClickCallback) {
      outsideClickCallback();
    }
  });

  useEffect(() => {
    if (!toggleGlobalState) return;

    document.body.classList.toggle("dd-active", isOpen);
  }, [isOpen, toggleGlobalState]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (disableGlobalCloseEvent) return;

    const handleOtherDropdownOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ id: string }>;

      if (customEvent.detail?.id === dropdownIdRef.current) return;

      setIsOpen(false);
    };

    window.addEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);

    return () => {
      window.removeEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
    };
  }, [disableGlobalCloseEvent]);

  useEffect(() => {
    if (!isOpen || disableGlobalCloseEvent) return;

    window.dispatchEvent(
      new CustomEvent(DROPDOWN_OPEN_EVENT, {
        detail: { id: dropdownIdRef.current },
      }),
    );
  }, [isOpen, disableGlobalCloseEvent]);

  return (
    <div
      ref={dropdownRef}
      className={classNames(
        "dropdown",
        {
          "dropdown--open": isOpen,
          "dropdown--to-right": toRight,
          "dropdown--align-right": alignRight,
          "dropdown--align-bottom": alignBottom,
          "dropdown--to-bottom": toBottom,
          "dropdown--from-right": fromRight,
          "dropdown--block": block,
          "dropdown--big": size === DropdownSize.Big,
        },
        className,
      )}
    >
      {children(isOpen, setIsOpen)}
    </div>
  );
}

const STICK_TO_BOTTOM_GAP = 16;

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  stickToBottom?: boolean;
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Which edge of the trigger the menu is aligned to when unpinned; stickToBottom keeps that same edge instead of always pinning to one side. */
  align?: "left" | "right";
}

export function DropdownMenu({
  children,
  className,
  isOpen = true,
  onClose,
  stickToBottom,
  triggerRef,
  align = "left",
  ...restProps
}: DropdownMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [stickyStyle, setStickyStyle] = useState<React.CSSProperties>();
  const naturalHeightRef = useRef<number>();

  useEffect(() => {
    if (isOpen) {
      setShowMenu(true);
    } else {
      const id = setTimeout(() => {
        onClose && onClose();
        setShowMenu(false);
        setStickyStyle(undefined);
      }, 200);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  const variant = useFreeSpaceFinder(menuRef, isOpen);
  const pos = "0";

  useLayoutEffect(() => {
    if (!stickToBottom || !isOpen || !menuRef.current) return;

    naturalHeightRef.current = menuRef.current.getBoundingClientRect().height;

    const updatePosition = () => {
      const trigger = triggerRef?.current ?? menuRef.current?.parentElement;
      if (!trigger || naturalHeightRef.current === undefined) return;

      const triggerRect = trigger.getBoundingClientRect();
      const naturalBottom = triggerRect.bottom + naturalHeightRef.current;

      if (naturalBottom > window.innerHeight - STICK_TO_BOTTOM_GAP) {
        // Align to the trigger's own edge (not the menu's current rect): a
        // portal-based menu (rc-dropdown) briefly resets itself to left:0
        // while it computes its own alignment, and measuring during that
        // reset pins the menu to the wrong place.
        setStickyStyle({
          position: "fixed",
          top: "auto",
          left: align === "left" ? triggerRect.left : "auto",
          right:
            align === "right" ? window.innerWidth - triggerRect.right : "auto",
          bottom: STICK_TO_BOTTOM_GAP,
        });
      } else {
        setStickyStyle(undefined);
      }
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, stickToBottom, triggerRef, align]);

  return isOpen || showMenu ? (
    <div
      className={classNames(
        "dropdown__menu",
        {
          "dropdown__menu--open": showMenu && isOpen,
        },
        className,
      )}
      {...restProps}
      ref={menuRef}
      style={{
        right: variant === "left" ? pos : undefined,
        left: variant === "right" ? pos : undefined,
        ...stickyStyle,
      }}
    >
      {children}
    </div>
  ) : null;
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  toggleGlobalState?: boolean;
  disabled?: boolean;
  dark?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export function DropdownMenuItem({
  children,
  className,
  toggleGlobalState,
  disabled,
  onClick = () => undefined,
  dark,
  ...rest
}: DropdownMenuItemProps) {
  return (
    <div
      className={classNames(
        "dropdown__menu-item",
        {
          "dropdown__menu-item--disabled": disabled,
          "dropdown__menu-item--dark": dark,
        },
        className,
      )}
      {...rest}
      onClick={(e) => {
        onClick(e);
        if (toggleGlobalState) {
          document.body.classList.remove("dd-active");
        }
      }}
    >
      {children}
    </div>
  );
}

interface DropdownMenuSeparatorProps {
  children?: React.ReactNode;
  dashed?: boolean;
}

export function DropdownMenuSeparator({
  children,
  dashed,
}: DropdownMenuSeparatorProps) {
  return (
    <div
      className={classNames("dropdown__menu-separator", {
        "dropdown__menu-separator--dashed": dashed,
      })}
    >
      {children}
    </div>
  );
}

interface DropdownMenuTitleProps {
  children: React.ReactNode;
}

export function DropdownMenuTitle({ children }: DropdownMenuTitleProps) {
  return <h3 className="dropdown__menu-title">{children}</h3>;
}

export const DropdownMenuIcon: any = ({ src }: any) => {
  return <img className="dropdown__menu-icon" src={src} role="presentation" />;
};
